import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import BaseLayout from '../../admin/BaseLayout'
import AuthPanel from '../../components/auth/AuthPanel'
import PolymarketHub from '../../components/oracle/PolymarketHub'
import QuantumOddsHub from '../../components/oracle/QuantumOddsHub'
import QuantumOddsCTA from '../../components/oracle/QuantumOddsCTA'
import OracleHome from '../../components/oracle/OracleHome'
import { OracleChatWelcome, OracleChatBanner } from '../../components/oracle/OracleChatWelcome'
import IconifyIcon from '../../components/wrapper/IconifyIcon'
import { useAuth } from '../../contexts/AuthContext'
import { API_BASE_URL } from '../../config/api'
import './Oracle.css'
import '../../components/oracle/OracleEntry.css'

const STARTERS = [
  {
    icon: 'solar:chart-2-bold-duotone',
    titleKey: 'findPatterns',
    defaultTitle: 'Encontrar padrões',
    promptKey: 'findPatternsPrompt',
    defaultPrompt: 'Que equipas da Premier League têm melhor forma recente e maior diferença positiva entre xG e golos sofridos?',
  },
  {
    icon: 'solar:users-group-rounded-bold-duotone',
    titleKey: 'compareTeams',
    defaultTitle: 'Comparar equipas',
    promptKey: 'compareTeamsPrompt',
    defaultPrompt: 'Compara Arsenal e Liverpool esta época: classificação, forma, golos, xG e confrontos diretos.',
  },
  {
    icon: 'solar:football-bold-duotone',
    titleKey: 'prepareMatch',
    defaultTitle: 'Preparar um jogo',
    promptKey: 'prepareMatchPrompt',
    defaultPrompt: 'Analisa o próximo jogo do Benfica: forma das equipas, ausências, provável onze, H2H e previsões disponíveis.',
  },
  {
    icon: 'solar:user-id-bold-duotone',
    titleKey: 'analysePlayer',
    defaultTitle: 'Analisar um jogador',
    promptKey: 'analysePlayerPrompt',
    defaultPrompt: 'Mostra o perfil, os pontos fortes e as estatísticas recentes de Viktor Gyökeres.',
  },
];

const TOOL_LABELS = {
  search_matches: 'A localizar jogos',
  get_match_detail: 'A abrir o detalhe do jogo',
  get_match_h2h: 'A comparar confrontos diretos',
  get_match_lineups: 'A consultar alinhamentos',
  get_match_incidents: 'A consultar acontecimentos',
  get_match_shotmap: 'A analisar remates e xG',
  search_teams: 'A localizar equipas',
  get_team_detail: 'A consultar o perfil da equipa',
  get_team_fixtures: 'A consultar forma e calendário',
  get_team_squad: 'A consultar plantel e ausências',
  search_players: 'A localizar jogadores',
  get_player_detail: 'A consultar o perfil do jogador',
  get_player_stats: 'A analisar estatísticas do jogador',
  list_leagues: 'A consultar competições',
  get_standings: 'A consultar a classificação',
  get_predictions: 'A consultar previsões',
  compare_odds: 'A comparar odds',
  get_best_odds: 'A procurar as melhores odds',
  list_bookmakers: 'A consultar bookmakers',
  get_polymarket_odds: 'A consultar a Polymarket',
  poly_list_soccer_leagues: 'A consultar ligas Polymarket',
  poly_search_soccer_events: 'A filtrar mercados de futebol',
  poly_get_event: 'A abrir mercados Polymarket',
  poly_get_market: 'A consultar o mercado',
  poly_get_orderbook: 'A consultar o orderbook',
  poly_search_trades: 'A pesquisar transações',
  poly_get_price_history: 'A consultar o histórico de preço',
  search_managers: 'A localizar treinadores',
  get_manager_detail: 'A analisar o treinador',
  list_referees: 'A consultar árbitros',
  list_venues: 'A consultar estádios',
  get_venue: 'A consultar o estádio',
  list_seasons: 'A consultar épocas',
  get_season: 'A consultar a época',
  list_social_items: 'A consultar notícias e conteúdos',
  list_tv_channels: 'A consultar canais oficiais',
  list_broadcasts: 'A procurar transmissões oficiais',
  sports_data: 'A consultar dados desportivos',
};

const parseSseStream = async (stream, onEvent) => {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const parseBlock = (block) => {
    let eventName = 'message';
    const dataLines = [];
    block.split('\n').forEach((line) => {
      if (line.startsWith('event:')) eventName = line.slice(6).trim();
      if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    });
    if (!dataLines.length) return true;
    return onEvent(eventName, JSON.parse(dataLines.join('\n'))) !== false;
  };

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done }).replace(/\r\n/g, '\n');
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const keepReading = parseBlock(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      if (!keepReading) {
        await reader.cancel();
        return;
      }
      boundary = buffer.indexOf('\n\n');
    }
    if (done) break;
  }
  if (buffer.trim()) parseBlock(buffer);
};

const formatRelativeDate = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
};

const makeMessage = (role, text, extra = {}) => ({ id: `${role}-${Date.now()}-${Math.random()}`, role, text, ...extra });

const getYouTubeVideoId = (href) => {
  try {
    const url = new URL(String(href || ''));
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    let videoId = '';
    if (host === 'youtu.be') videoId = url.pathname.split('/').filter(Boolean)[0] || '';
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      if (url.pathname === '/watch') videoId = url.searchParams.get('v') || '';
      else if (/^\/(embed|shorts|live)\//.test(url.pathname)) videoId = url.pathname.split('/')[2] || '';
    }
    return /^[A-Za-z0-9_-]{11}$/.test(videoId) ? videoId : '';
  } catch (_error) {
    return '';
  }
};

const textFromMarkdownNode = (node) => {
  if (!node) return '';
  if (node.type === 'text') return node.value || '';
  return (node.children || []).map(textFromMarkdownNode).join('');
};

const YouTubePreview = ({ href, label, videoId }) => {
  const { t } = useTranslation();
  const tr = (key, defaultValue, options = {}) => Object.entries(options).reduce((text, [name, value]) => text.split(`{{${name}}}`).join(String(value)), t(`oracleCurrent.${key}`, { defaultValue, ...options }));
  const [playing, setPlaying] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const title = label || tr('video.defaultTitle', 'Highlights do jogo');

  return (
    <figure className="oracle-video-card">
      {playing ? (
        <div className="oracle-video-frame">
          <iframe
            src={'https://www.youtube-nocookie.com/embed/' + videoId + '?autoplay=1&rel=0'}
            title={title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      ) : (
        <button type="button" className={'oracle-video-preview ' + (thumbnailFailed ? 'no-thumbnail' : '')} onClick={() => setPlaying(true)} aria-label={tr('video.playLabel', 'Reproduzir {{title}}', { title })}>
          {!thumbnailFailed && (
            <img
              src={'https://i.ytimg.com/vi/' + videoId + '/hqdefault.jpg'}
              alt=""
              loading="lazy"
              onError={() => setThumbnailFailed(true)}
            />
          )}
          <span className="oracle-video-play"><IconifyIcon icon="solar:play-bold" width="24" /></span>
          <strong>{title}</strong>
          <small>{tr('video.clickToPlay', 'Clica para reproduzir')}</small>
        </button>
      )}
      <figcaption>
        <a className="oracle-video-title" href={href} target="_blank" rel="noopener noreferrer">{title}</a>
        <a href={href} target="_blank" rel="noopener noreferrer">{tr('video.watchOnYouTube', 'Ver no YouTube')}</a>
      </figcaption>
    </figure>
  );
};

const OracleMarkdownParagraph = ({ node, children }) => {
  const meaningfulChildren = (node?.children || []).filter((child) => child.type !== 'text' || child.value.trim());
  const onlyChild = meaningfulChildren.length === 1 ? meaningfulChildren[0] : null;
  const href = onlyChild?.tagName === 'a' ? String(onlyChild.properties?.href || '') : '';
  const videoId = getYouTubeVideoId(href);
  if (videoId) return <YouTubePreview href={href} videoId={videoId} label={textFromMarkdownNode(onlyChild).trim()} />;
  return <p>{children}</p>;
};

const OracleMarkdownLink = ({ href = '', children }) => {
  if (/^\//.test(href)) return <a href={href}>{children}</a>;
  if (!/^https?:\/\//i.test(href)) return <span>{children}</span>;
  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
};

const Oracle = () => {
  const { t, i18n } = useTranslation();
  const tr = (key, defaultValue, options = {}) => Object.entries(options).reduce((text, [name, value]) => text.split(`{{${name}}}`).join(String(value)), t(`oracleCurrent.${key}`, { defaultValue, ...options }));
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-GB' : i18n.resolvedLanguage?.startsWith('es') ? 'es-ES' : 'pt-PT';
  const localized = /^(en|es)/.test(i18n.resolvedLanguage || i18n.language || '');
  const { user, signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const starters = STARTERS.map((starter) => ({ ...starter, title: tr(`starters.${starter.titleKey}`, starter.defaultTitle), prompt: tr(`starters.${starter.promptKey}`, starter.defaultPrompt) }));
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const oracleView = requestedView === 'prelive' ? 'chat' : ['polymarket', 'quantumodds'].includes(requestedView) ? requestedView : 'home';
  const setOracleView = (view) => setSearchParams(view === 'home' ? {} : { view: view === 'chat' ? 'prelive' : view });
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [usage, setUsage] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [activity, setActivity] = useState('');
  const [activeTools, setActiveTools] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.matchMedia('(min-width: 769px)').matches);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const abortRef = useRef(null);
  const textareaRef = useRef(null);
  const navigationRef = useRef(null);
  const conversationRef = useRef(null);
  const pendingScrollRef = useRef(null);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 769px)');
    const updateSidebar = () => setSidebarOpen(desktop.matches);
    desktop.addEventListener('change', updateSidebar);
    return () => desktop.removeEventListener('change', updateSidebar);
  }, []);

  const closeHistoryOnMobile = () => {
    if (window.matchMedia('(max-width: 768px)').matches) setSidebarOpen(false);
  };

  const authenticatedFetch = async (path, options = {}) => {
    const token = await user.getIdToken();
    return fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    });
  };

  const refreshOverview = async () => {
    if (!user) {
      setConversations([]);
      setConversationId(null);
      setMessages([]);
      setUsage(null);
      setLoadingHistory(false);
      return;
    }
    setLoadingHistory(true);
    try {
      const [conversationsResponse, usageResponse] = await Promise.all([
        authenticatedFetch('/api/oracle/conversations?limit=30'),
        authenticatedFetch('/api/oracle/usage'),
      ]);
      if (conversationsResponse.ok) {
        const data = await conversationsResponse.json();
        setConversations(data.conversations || []);
      }
      if (usageResponse.ok) {
        const data = await usageResponse.json();
        setUsage(data.usage || null);
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    refreshOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const mode = pendingScrollRef.current;
    const conversation = conversationRef.current;
    if (!mode || !conversation) return;
    pendingScrollRef.current = null;
    if (mode === 'top') {
      conversation.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }
    const userMessages = conversation.querySelectorAll('.oracle-message.is-user');
    const latestQuestion = userMessages[userMessages.length - 1];
    if (latestQuestion) {
      const conversationTop = conversation.getBoundingClientRect().top;
      const questionTop = latestQuestion.getBoundingClientRect().top;
      const targetTop = conversation.scrollTop + questionTop - conversationTop - 16;
      conversation.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
    }
  }, [messages]);

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 160)}px`;
  }, [input, oracleView]);

  useEffect(() => {
    if (oracleView === 'home') return;
    const navigation = navigationRef.current;
    const active = navigation?.querySelector('.is-active');
    if (!active) return;
    const bounds = navigation.getBoundingClientRect();
    const selected = active.getBoundingClientRect();
    if (selected.left < bounds.left) navigation.scrollLeft -= bounds.left - selected.left;
    else if (selected.right > bounds.right) navigation.scrollLeft += selected.right - bounds.right;
  }, [oracleView]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const usagePercent = useMemo(() => {
    if (!usage?.user?.limit) return 0;
    return Math.min(100, Math.round((usage.user.used / usage.user.limit) * 100));
  }, [usage]);

  const startNewConversation = () => {
    setOracleView('chat');
    if (streaming) return;
    pendingScrollRef.current = 'top';
    setConversationId(null);
    setMessages([]);
    setInput('');
    setError('');
    setActivity('');
    setActiveTools([]);
    closeHistoryOnMobile();
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const openAuthModal = () => {
    setAuthError('');
    setShowAuthModal(true);
  };

  const handleGoogleSignIn = async () => {
    try {
      setAuthLoading(true);
      setAuthError('');
      await signInWithGoogle();
      setShowAuthModal(false);
      requestAnimationFrame(() => textareaRef.current?.focus());
    } catch (requestError) {
      setAuthError(tr('errors.googleSignIn', 'Falha ao entrar com Google: {{message}}', { message: requestError.message }));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleEmailAuth = async (event) => {
    event.preventDefault();
    try {
      setAuthLoading(true);
      setAuthError('');
      if (isSignUp) {
        await signUpWithEmail(authEmail, authPassword);
      } else {
        await signInWithEmail(authEmail, authPassword);
      }
      setShowAuthModal(false);
      requestAnimationFrame(() => textareaRef.current?.focus());
    } catch (requestError) {
      setAuthError(tr(isSignUp ? 'errors.signUp' : 'errors.signIn', `${isSignUp ? 'Registo' : 'Login'} falhou: {{message}}`, { message: requestError.message }));
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        startNewConversation();
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  });

  const openConversation = async (id) => {
    setOracleView('chat');
    if (streaming || id === conversationId) return;
    setLoadingHistory(true);
    setError('');
    try {
      const response = await authenticatedFetch(`/api/oracle/conversations/${encodeURIComponent(id)}`);
      const data = await response.json();
      if (!response.ok) throw new Error(localized ? tr('errors.openConversation', 'Não foi possível abrir esta conversa.') : (data.message || tr('errors.openConversation', 'Não foi possível abrir esta conversa.')));
      const restored = (data.turns || []).flatMap((turn) => [
        makeMessage('user', turn.question, { createdAt: turn.askedAt }),
        makeMessage('assistant', turn.answer || (turn.status === 'failed' ? tr('errors.incompleteResponse', 'Esta resposta não foi concluída.') : ''), {
          createdAt: turn.completedAt,
          tools: turn.tools || [],
          failed: turn.status === 'failed',
          polymarketIntent: turn.polymarketIntent || null,
        }),
      ]);
      setConversationId(id);
      pendingScrollRef.current = 'top';
      setMessages(restored);
      closeHistoryOnMobile();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoadingHistory(false);
    }
  };

  const updateStreamingMessage = (messageId, updater) => {
    setMessages((current) => current.map((message) => message.id === messageId ? updater(message) : message));
  };

  const submitPrompt = async (rawPrompt) => {
    setOracleView('chat');
    const prompt = String(rawPrompt || '').trim();
    if (!prompt || streaming) return;
    if (!user) {
      setInput(prompt);
      openAuthModal();
      return;
    }

    const assistantId = `assistant-${Date.now()}`;
    pendingScrollRef.current = 'question';
    setMessages((current) => [
      ...current,
      makeMessage('user', prompt),
      { id: assistantId, role: 'assistant', text: '', streaming: true, tools: [] },
    ]);
    setInput('');
    setError('');
    setStreaming(true);
    setActivity(tr('status.thinking', 'A interpretar a tua pergunta…'));
    setActiveTools([]);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await authenticatedFetch('/api/oracle/chat', {
        method: 'POST',
        body: JSON.stringify({ message: prompt, conversationId }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw Object.assign(new Error(localized ? tr('errors.oracleFailed', 'O Oráculo não conseguiu responder.') : (data.message || tr('errors.oracleFailed', 'O Oráculo não conseguiu responder.'))), { code: data.code });
      }

      await parseSseStream(response.body, (type, data) => {
        if (type === 'ready') {
          setConversationId(data.conversationId);
        }
        if (type === 'status') setActivity(localized ? tr(`status.${data.phase || 'analysing'}`, 'A analisar…') : (data.message || 'A analisar…'));
        if (type === 'tool') {
          const label = tr(`tools.${data.name}`, TOOL_LABELS[data.name] || 'A consultar dados');
          setActivity(data.state === 'completed' ? tr('status.organizing', 'A organizar a resposta…') : `${label}…`);
          if (data.state === 'started') {
            setActiveTools((current) => current.includes(label) ? current : [...current, label]);
            updateStreamingMessage(assistantId, (message) => ({
              ...message,
              tools: message.tools.includes(data.name) ? message.tools : [...message.tools, data.name],
            }));
          }
        }
        if (type === 'delta') {
          setActivity('');
          updateStreamingMessage(assistantId, (message) => ({ ...message, text: message.text + (data.text || '') }));
        }
        if (type === 'done') {
          if (data.usage) setUsage(data.usage);
          updateStreamingMessage(assistantId, (message) => ({ ...message, streaming: false, mcpCallCount: data.mcpCallCount }));
          setStreaming(false);
          setActivity('');
          setActiveTools([]);
          abortRef.current = null;
          return false;
        }
        if (type === 'error') throw new Error(localized ? tr('errors.responseInterrupted', 'A resposta foi interrompida.') : (data.message || tr('errors.responseInterrupted', 'A resposta foi interrompida.')));
        return true;
      });

      refreshOverview().catch(() => {});
    } catch (requestError) {
      const stopped = requestError.name === 'AbortError';
      const message = stopped ? tr('errors.interrupted', 'Resposta interrompida.') : requestError.message;
      setError(stopped ? '' : message);
      updateStreamingMessage(assistantId, (current) => ({
        ...current,
        streaming: false,
        failed: true,
        text: current.text || message,
      }));
    } finally {
      setStreaming(false);
      setActivity('');
      setActiveTools([]);
      abortRef.current = null;
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitPrompt(input);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitPrompt(input);
    }
  };

  const copyAnswer = async (text) => {
    await navigator.clipboard?.writeText(text);
  };

  return (
    <>
      <BaseLayout contentClassName="oracle-app-container">
      {oracleView === 'home' && <OracleHome onSubmit={submitPrompt} />}
      <main className={`oracle-page ${sidebarOpen ? 'has-open-history' : ''} ${oracleView === 'home' ? 'oracle-section-hidden' : ''}`}>
        <button
          type="button"
          className={`oracle-sidebar-scrim ${sidebarOpen ? 'is-visible' : ''}`}
          aria-label={tr('closeHistory', 'Fechar histórico')}
          onClick={() => setSidebarOpen(false)}
        />

        <aside id="oracle-history-panel" className={`oracle-sidebar ${sidebarOpen ? 'is-open' : ''}`} aria-hidden={!sidebarOpen} inert={!sidebarOpen}>
          <div className="oracle-sidebar-top">
            <div className="oracle-brand-lockup">
              <div><strong>{tr('brand', 'Oráculo')}</strong><small>{tr('brandSubtitle', 'Football intelligence')}</small></div>
            </div>
            <button className="oracle-icon-button" type="button" onClick={() => setSidebarOpen(false)} aria-label={tr('closeHistory', 'Fechar histórico')} aria-controls="oracle-history-panel" aria-expanded={sidebarOpen}>
              <svg className="oracle-desktop-sidebar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="3" /><path d="M9 4v16" /></svg>
              <IconifyIcon className="oracle-mobile-sidebar-icon" icon="solar:close-circle-linear" width="22" />
            </button>
          </div>

          <button className="oracle-new-chat" type="button" onClick={startNewConversation} disabled={streaming}>
            <IconifyIcon icon="solar:pen-new-square-linear" width="19" />
            {tr('newAnalysis', 'Nova análise')}
            <span>⌘ K</span>
          </button>

          <div className="oracle-history-heading"><span>{tr('recent', 'Recentes')}</span><small>{user ? conversations.length : 0}</small></div>
          <div className="oracle-history-list">
            {!user && <div className="oracle-history-empty">{tr('historyLogin', 'Entra para veres o teu histórico de análises.')}</div>}
            {user && loadingHistory && !conversations.length && <div className="oracle-history-loading">{tr('historySync', 'A sincronizar histórico…')}</div>}
            {user && !loadingHistory && !conversations.length && <div className="oracle-history-empty">{tr('historyEmpty', 'As tuas análises ficam guardadas aqui.')}</div>}
            {user && conversations.map((conversation) => (
              <button
                type="button"
                key={conversation._id}
                className={`oracle-history-item ${conversationId === conversation._id ? 'is-active' : ''}`}
                onClick={() => openConversation(conversation._id)}
              >
                <span>{conversation.title}</span>
                <small>{formatRelativeDate(conversation.updatedAt, locale)}</small>
              </button>
            ))}
          </div>

          <div className="oracle-sidebar-footer">
            <div className="oracle-quota-row">
              <span className="oracle-quota-ring" style={{ '--usage': `${usagePercent * 3.6}deg` }}><i /></span>
              <div>
                <span>{tr('dailyUsage', 'Uso diário')}</span>
                <strong>{!user ? tr('availableAfterLogin', 'Disponível após login') : usage ? tr('usageRemaining', '{{remaining}} de {{limit}}', { remaining: usage.user.remaining, limit: usage.user.limit }) : tr('calculating', 'A calcular…')}</strong>
              </div>
            </div>
          </div>
        </aside>

        <section className={`oracle-workspace ${oracleView === 'chat' && !messages.length ? 'is-prelive-empty' : ''}`}>
          <header className="oracle-workspace-header">
            <button className="oracle-icon-button oracle-history-toggle" type="button" onClick={() => setSidebarOpen((open) => !open)} aria-label={tr('openHistory', 'Abrir histórico')} aria-controls="oracle-history-panel" aria-expanded={sidebarOpen}>
              <svg className="oracle-desktop-sidebar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="3" /><path d="M9 4v16" /></svg>
              <IconifyIcon className="oracle-mobile-sidebar-icon" icon="solar:hamburger-menu-linear" width="22" />
            </button>
            <div className="oracle-brand-lockup">
              <div><strong>{tr('brand', 'Oráculo')}</strong><small>{tr('brandSubtitle', 'Football intelligence')}</small></div>
            </div>
            <nav ref={navigationRef} className="oracle-section-nav" aria-label={t('oracleChat.sectionsLabel')}>
              <Link to="/oracle">{t('oracleChat.home')}</Link>
              <button type="button" className={oracleView === 'chat' ? 'is-active' : ''} onClick={() => setOracleView('chat')}>{t('oracleHome.cards.prelive.title')}</button>
              <button type="button" className={oracleView === 'polymarket' ? 'is-active' : ''} onClick={() => setOracleView('polymarket')}>{t('oracleHome.cards.polymarket.title')}</button>
              <button type="button" className={oracleView === 'quantumodds' ? 'is-active' : ''} onClick={() => setOracleView('quantumodds')}>{t('oracleHome.cards.quantumodds.title')}</button>
            </nav>
          </header>

          <div className={`oracle-pm-panel ${oracleView === 'polymarket' ? '' : 'oracle-section-hidden'}`}><PolymarketHub active={oracleView === 'polymarket'} authenticatedFetch={authenticatedFetch} user={user} onRequireAuth={openAuthModal} /></div>
          <div className={`oracle-pm-panel ${oracleView === 'quantumodds' ? '' : 'oracle-section-hidden'}`}><QuantumOddsHub active={oracleView === 'quantumodds'} authenticatedFetch={authenticatedFetch} user={user} /></div>
          <div ref={conversationRef} className={`oracle-conversation ${messages.length ? 'has-messages' : 'is-empty'} ${oracleView !== 'chat' ? 'oracle-section-hidden' : ''}`}>
            {messages.length > 0 && (
              <div className="oracle-message-list" aria-live="polite">
                {messages.map((message) => (
                  <article className={`oracle-message is-${message.role} ${message.failed ? 'is-failed' : ''}`} key={message.id}>
                    <div className="oracle-message-avatar">
                      {message.role === 'assistant' ? 'FM' : (user?.displayName || user?.email || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="oracle-message-content">
                      <div className="oracle-message-label">{message.role === 'assistant' ? tr('brand', 'Oráculo') : tr('you', 'Tu')}</div>
                      {message.text ? (
                        <div className="oracle-markdown">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: OracleMarkdownLink, p: OracleMarkdownParagraph }}>{message.text}</ReactMarkdown>
                          {message.role === 'assistant' && !message.streaming && !message.failed && !message.text.includes('quantumodds.xyz') && <QuantumOddsCTA />}
                        </div>
                      ) : message.streaming ? (
                        <div className="oracle-thinking"><i /><i /><i /></div>
                      ) : null}
                      {message.role === 'assistant' && message.text && !message.streaming && (
                        <button className="oracle-copy" type="button" onClick={() => copyAnswer(message.text)} aria-label={tr('copyAnswer', 'Copiar resposta')}>
                          <IconifyIcon icon="solar:copy-linear" width="16" /> {tr('copy', 'Copiar')}
                        </button>
                      )}
                    </div>
                  </article>
                ))}
                {streaming && activity && (
                  <div className="oracle-activity"><span><i /></span><strong>{activity}</strong>{activeTools.length > 1 && <small>{tr('completedQueries', '{{count}} consultas concluídas', { count: activeTools.length })}</small>}</div>
                )}
                <div />
              </div>
            )}
          </div>

          <div className={`oracle-composer-zone ${oracleView !== 'chat' ? 'oracle-section-hidden' : ' '}`}>
            {!messages.length && <OracleChatWelcome oracle="prelive" active={oracleView === 'chat'} onSelect={(prompt) => { setInput(prompt); textareaRef.current?.focus() }} />}
            {error && <div className="oracle-error"><IconifyIcon icon="solar:danger-triangle-linear" width="18" />{error}</div>}
            <form className="oracle-composer" onSubmit={handleSubmit}>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(event) => setInput(event.target.value.slice(0, 2000))}
                onKeyDown={handleKeyDown}
                placeholder={tr('composerPlaceholder', 'Pergunta sobre uma equipa, jogador, jogo ou competição…')}
                rows="1"
                disabled={streaming}
                aria-label={tr('askOracle', 'Pergunta ao Oráculo')}
              />
              <div className="oracle-composer-footer">
                {messages.length > 0 && <div><IconifyIcon icon="solar:database-linear" width="15" /> {tr('verifiedData', 'Dados verificados')}</div>}
                {streaming ? (
                  <button type="button" className="oracle-stop-button" onClick={() => abortRef.current?.abort()} aria-label={tr('stopAnswer', 'Parar resposta')}>
                    <span />
                  </button>
                ) : (
                  <button type="submit" className={`oracle-send-button ${!messages.length ? 'oracle-send-text' : ''}`} disabled={!input.trim()} aria-label={tr('sendQuestion', 'Enviar pergunta')}>
                    {messages.length ? <IconifyIcon icon="solar:arrow-up-linear" width="20" /> : t('oracleChat.send')}
                  </button>
                )}
              </div>
            </form>
            {!messages.length && <div className="oracle-prompt-suggestions" aria-label={t('oracleChat.preliveSuggestionsLabel')}>
              {starters.map((starter) => <button type="button" key={starter.title} onClick={() => submitPrompt(starter.prompt)}>{starter.prompt}</button>)}
            </div>}
            {!messages.length && <OracleChatBanner />}
            {messages.length > 0 && <p className="oracle-disclaimer">{tr('disclaimer', 'O Oráculo pode interpretar dados incorretamente. Confirma sempre valores importantes. As perguntas ficam registadas para melhorar o serviço.')}</p>}
          </div>
        </section>
      </main>
      </BaseLayout>
      {showAuthModal && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 2200 }}>
          <div className="modal-dialog">
            <div className="modal-content filterbet-auth-modal">
              <AuthPanel
                email={authEmail}
                password={authPassword}
                error={authError}
                isSignUp={isSignUp}
                loading={authLoading}
                onEmailChange={setAuthEmail}
                onGoogleSignIn={handleGoogleSignIn}
                onPasswordChange={setAuthPassword}
                onRequestClose={() => setShowAuthModal(false)}
                onSubmit={handleEmailAuth}
                onToggleMode={() => setIsSignUp((current) => !current)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Oracle;
